import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('goat_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('goat_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1, selectedSize = null, selectedColour = null) => {
    setCart(currentCart => {
      const existingItemIndex = currentCart.findIndex(
        item => 
          item.productId === product.id && 
          item.size === selectedSize && 
          item.colour === selectedColour
      );

      if (existingItemIndex > -1) {
        const updatedCart = [...currentCart];
        updatedCart[existingItemIndex].quantity += quantity;
        return updatedCart;
      }

      return [...currentCart, {
        productId: product.id,
        productName: product.name,
        price: product.price,
        imageUrl: product.imageUrl,
        size: selectedSize,
        colour: selectedColour,
        quantity
      }];
    });
  };

  const removeFromCart = (productId, size, colour) => {
    setCart(currentCart => 
      currentCart.filter(item => 
        !(item.productId === productId && item.size === size && item.colour === colour)
      )
    );
  };

  const updateQuantity = (productId, size, colour, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId, size, colour);
      return;
    }

    setCart(currentCart =>
      currentCart.map(item =>
        item.productId === productId && item.size === size && item.colour === colour
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
